import axios from 'axios'
import queryString from 'query-string'

import { ANYWHERE, DEFAULT_RADIUS } from '../helpers/constants'
import { parseLoadDetails } from '../helpers/load'
import { PaginatedResults } from '../helpers/typeHelper'
import { toCamelCase, toSnakeCase } from '../helpers/utils'
import { Colors } from '../theme/colors'
import { EquipmentType, OwnershipType } from './equipment'
import { JobInvoice } from './jobInvoice'
import { LeasingPartnerName } from './leasingPartner'
import {
  DistanceTypes,
  Load,
  LoadBookingTypes,
  LoadStopProgress,
  LoadStopProgressStatus,
  LocationVector,
  ProviderEta,
  StopCheckin,
  TrackingStatus,
} from './load'
import { SortType } from './search'
import { SlimTeam } from './team'

export interface JobListAssignedTruck {
  id: string
  leasingPartner?: LeasingPartnerName
  ownershipType?: OwnershipType
}

export enum TrackingStatusDetail {
  EARLY = 'Early',
  ON_TIME = 'On time',
  MAYBE_LATE = 'Maybe late',
  LATE = 'Late',
  AT_FACILITY = 'At facility',
  DEPARTED = 'Departed',
  NO_LOCATION = 'No location',
  SCHEDULED = 'Scheduled',
  TRACKING_UNAVAILABLE = 'Tracking unavailable',
}

export interface JobListJob {
  assignedTo: AssignedUser
  assignedAgent: AssignedUser | null
  assignedTruck: JobListAssignedTruck | null
  humanId: string
  id: string
  load: {
    broker: {
      bolSubmissionMethod: string
      id: string
      name: string
    } | null
    destCity: string
    destDeliveryDate: string
    destState: string
    id: string
    originCity: string
    originPickupDate: string
    originState: string
    source: string
    sourceHumanId: string
  }
  productSubscription: {
    bundleName: string
    carrier: string | null
  }
  status: JobStatus
  statusDetail: JobStatusDetail
  team: {
    id: string
    name: string
  }
  eldTrackingStatus?: TrackingStatus
  unsolvedTasksCount?: number
  hosEldTrackingStatus?: TrackingStatus
  trackingStatusDetail?: TrackingStatusDetail
}

export interface Job {
  assignedTo?: AssignedUser
  assignedTruck?: AssignedTruck
  bookingType: BookingTypes
  brokerPolicy: string
  confirmedRate: number
  createdBy: string
  notes: string
  dateCreated: string
  dateUpdated: string
  humanId: string
  id: string
  status: JobStatus
  load: Load
  statements: Statement[]
  statusDetail: JobStatusDetail
  humanStatusDetail: string
  humanStatusDetailColor: string
  documents?: Document[]
  userSubmittedBidMax?: number
  userSubmittedBidMin?: number
  isCanceled: boolean
  isFactored: boolean
  isApproved: boolean
  isDeliveryRejected: boolean
  isInProgress: boolean
  showCancelAction: boolean
  showRequestCancellationAction: boolean
  showCompleteAction: boolean
  showDeleteAction: boolean
  showEditJobAction: boolean
  showRemoveAction: boolean
  showReportIssueAction: boolean
  showUploadDocumentAction: boolean
  showAssignToAction: boolean
  showTrackingAction: boolean
  showCreateInvoiceAction: boolean
  showNotifyFactoringCompanyAction: boolean
  loadStopProgresses?: LoadStopProgress[]
  eldLoadStopProgresses?: LoadStopProgress[]
  trackingCurrentLoadStopProgress?: LoadStopProgress
  trackingStatus?: TrackingStatus
  trackingType?: JobTrackingType
  sentByEmail?: boolean
  invoices: JobInvoice[]
  team?: SlimTeam
  latestLocationVector?: LocationVector
  assignedAgent?: AssignedUser
}

export interface Document {
  dateCreated: string
  dateUpdated: string
  description: string
  isEditable: boolean
  id: string
  job: string
  type: DocumentType
  uploadedBy: string
  attachments: Attachment[]
}

export interface Attachment {
  file: string
  mimetype: string
  originalFilename: string
}

export interface Statement {
  dateCreated: string
  dateUpdated: string
  description: string
  id: string
  leaseFee: number
  leaseFeePercent: number
  lineItems: LineItem[]
  serviceFee: number
  serviceFeePercent: number
  subtotal: number
  total: number
  feeItems: FeeItem[]
  type: string
}

export interface LineItem {
  amount: string
  dateCreated: string
  dateUpdated: string
  description: string
  type: string
}

export interface FeeItem {
  description: string
  amount: string
  dateCreated: string
  dateUpdated: string
}

export interface AssignedUser {
  id: string
  firstName: string
  lastName: string
  email?: string
}

export interface AssignedTruck {
  id: string
  vinNumber: string
  leasingPartner?: LeasingPartnerName
  ewnershipType?: OwnershipType
}

export interface GenericLoadStopProgress {
  stop: any
  status: LoadStopProgressStatus
  arrivalStopCheckin?: StopCheckin
  departureStopCheckin?: StopCheckin
  providerEta?: ProviderEta
  dateEstimatedArrival?: string
}

export interface QueryFilter {
  origin_location?: string
  origin_range_mi__max: number
  origin_pickup_date__min: string
  dest_location?: string
  dest_range_mi__max?: number
  equipment?: EquipmentType[]
  total_pay__min?: number
  trip_rate__min?: number
  sort_type?: SortType
  booking_type: string
  trip_distance_mi__max?: number
  trip_distance_mi__min?: number
  trip_distances?: string[]
  date_queried?: string
  hide_sort_info?: boolean
  load_providers?: Array<string>
  age_min__min?: number
  age_min__max?: number
  truck_weight_lb__max?: number
  requested_states?: string[]
  is_offline_book_compatible?: boolean
  masked_data?: boolean
  async?: boolean
}

export interface PastLocationVectors {
  pastEldLocationVectors: LocationVector[]
  pastMobileLocationVectors: LocationVector[]
}

export enum JobTrackingType {
  MOBILE = 'MOBILE',
  ELD = 'ELD',
}

export enum JobStatusDetail {
  BIDDING_REQUIRED = 'BIDDING_REQUIRED',
  BOOKED_OFFLINE = 'BOOKED_OFFLINE',
  ACTIVE_NEGOTIATION = 'ACTIVE_NEGOTIATION',
  BROKER_SETUP_REQUIRED = 'BROKER_SETUP_REQUIRED',
  ASYNC_INSTANT_BOOK_REQUESTED = 'ASYNC_INSTANT_BOOK_REQUESTED',
  AWAITING_RATE_CONFIRMATION = 'AWAITING_RATE_CONFIRMATION',
  REVIEWING_RATE_CONFIRMATION = 'REVIEWING_RATE_CONFIRMATION',
  ABORTED = 'ABORTED',
  JOB_APPROVED = 'JOB_APPROVED',
  PAYMENT_ISSUED = 'PAYMENT_ISSUED',
  CANCELED = 'CANCELED',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING_DELIVERY_VERIFICATION = 'PENDING_DELIVERY_VERIFICATION',
  PENDING_PAYMENT_VERIFICATION = 'PENDING_PAYMENT_VERIFICATION',
  PAYMENT_REQUIRED = 'PAYMENT_REQUIRED',
  DELIVERY_VERIFICATION_REJECTED = 'DELIVERY_VERIFICATION_REJECTED',
  PENDING_CANCELLATION = 'PENDING_CANCELLATION',
  REQUESTED_CANCELLATION = 'REQUESTED_CANCELLATION',
  RECOVERED = 'RECOVERED',
  NEEDS_RECOVERY = 'NEEDS_RECOVERY',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
}

export const JobConsoleStatusDetailUserFacingMap: Record<JobStatusDetail, string> = {
  [JobStatusDetail.BIDDING_REQUIRED]: 'Bidding Required',
  [JobStatusDetail.BOOKED_OFFLINE]: 'Booked Offline',
  [JobStatusDetail.ACTIVE_NEGOTIATION]: 'Active Negotiation',
  [JobStatusDetail.BROKER_SETUP_REQUIRED]: 'Broker Setup Required',
  [JobStatusDetail.ASYNC_INSTANT_BOOK_REQUESTED]: 'Async Instant Book Requested',
  [JobStatusDetail.AWAITING_RATE_CONFIRMATION]: 'Awaiting Rate Confirmation',
  [JobStatusDetail.REVIEWING_RATE_CONFIRMATION]: 'Reviewing Rate Confirmation',
  [JobStatusDetail.ABORTED]: 'Aborted',
  [JobStatusDetail.JOB_APPROVED]: 'Job Approved',
  [JobStatusDetail.IN_PROGRESS]: 'In Progress',
  [JobStatusDetail.PENDING_DELIVERY_VERIFICATION]: 'Pending Delivery Verification',
  [JobStatusDetail.PAYMENT_REQUIRED]: 'Payment Required',
  [JobStatusDetail.DELIVERY_VERIFICATION_REJECTED]: 'Delivery Verification Rejected',
  [JobStatusDetail.PENDING_PAYMENT_VERIFICATION]: 'Pending Payment Verification',
  [JobStatusDetail.PAYMENT_ISSUED]: 'Payment Issued',
  [JobStatusDetail.CANCELED]: 'Canceled',
  [JobStatusDetail.REQUESTED_CANCELLATION]: 'Cancellation Requested',
  [JobStatusDetail.PENDING_CANCELLATION]: 'Pending Cancellation',
  [JobStatusDetail.RECOVERED]: 'Recovered',
  [JobStatusDetail.NEEDS_RECOVERY]: 'Recovery Needed',
  [JobStatusDetail.ACTION_REQUIRED]: 'Action Required',
}

export function getJobConsoleStatusDetail(jobStatus: JobStatusDetail, humanStatus?: string) {
  const status = JobConsoleStatusDetailUserFacingMap[jobStatus]
  if (status) return status
  return humanStatus || jobStatus
}

export const JobStatusDetailColorMap: Record<JobStatusDetail, string> = {
  [JobStatusDetail.BIDDING_REQUIRED]: Colors.warningD1,
  [JobStatusDetail.BOOKED_OFFLINE]: Colors.warningD1,
  [JobStatusDetail.ACTIVE_NEGOTIATION]: Colors.warningD1,
  [JobStatusDetail.BROKER_SETUP_REQUIRED]: Colors.warningD1,
  [JobStatusDetail.ASYNC_INSTANT_BOOK_REQUESTED]: Colors.warningD1,
  [JobStatusDetail.AWAITING_RATE_CONFIRMATION]: Colors.warningD1,
  [JobStatusDetail.REVIEWING_RATE_CONFIRMATION]: Colors.warningD1,
  [JobStatusDetail.PENDING_DELIVERY_VERIFICATION]: Colors.warningD1,
  [JobStatusDetail.PAYMENT_REQUIRED]: Colors.warningD1,
  [JobStatusDetail.PENDING_PAYMENT_VERIFICATION]: Colors.warningD1,
  [JobStatusDetail.REQUESTED_CANCELLATION]: Colors.warningD1,
  [JobStatusDetail.PENDING_CANCELLATION]: Colors.warningD1,
  [JobStatusDetail.JOB_APPROVED]: Colors.successD1,
  [JobStatusDetail.IN_PROGRESS]: Colors.successD1,
  [JobStatusDetail.PAYMENT_ISSUED]: Colors.successD1,
  [JobStatusDetail.DELIVERY_VERIFICATION_REJECTED]: Colors.warningD1,
  [JobStatusDetail.CANCELED]: Colors.midGray,
  [JobStatusDetail.ABORTED]: Colors.midGray,
  [JobStatusDetail.RECOVERED]: Colors.warningD1,
  [JobStatusDetail.NEEDS_RECOVERY]: Colors.warningD1,
  [JobStatusDetail.ACTION_REQUIRED]: Colors.warningD1,
}

export enum JobStatus {
  PENDING = 'PENDING',
  CURRENT = 'CURRENT',
  FUTURE = 'FUTURE',
  PAST = 'PAST',
}

export enum BookingTypes {
  INSTANT = 'INSTANT_BOOK',
  STANDARD = 'STANDARD_BOOK',
  OFFLINE = 'OFFLINE',
  SUBMIT_BID = 'SUBMIT_BID',
  EXTERNAL_BOOK = 'EXTERNAL_BOOK',
  ALL = 'ALL',
}

export enum JobsFilter {
  JOB_LIST = 'JOB_LIST',
  PAST = 'PAST',
  CONTACTED_LOADS = 'CONTACTED_LOADS',
}

export enum DocumentType {
  BILL_OF_LADING = 'BILL_OF_LADING',
  RATE_CONFIRMATION = 'RATE_CONFIRMATION',
  LUMPER_RECEIPT = 'LUMPER_RECEIPT',
  OTHER = 'OTHER',
}

export const JOB_STATUS_DICT = {
  PENDING: [
    JobStatusDetail.BIDDING_REQUIRED,
    JobStatusDetail.ACTIVE_NEGOTIATION,
    JobStatusDetail.BOOKED_OFFLINE,
    JobStatusDetail.BROKER_SETUP_REQUIRED,
    JobStatusDetail.ASYNC_INSTANT_BOOK_REQUESTED,
    JobStatusDetail.AWAITING_RATE_CONFIRMATION,
    JobStatusDetail.REVIEWING_RATE_CONFIRMATION,
  ],
  FUTURE: [JobStatusDetail.JOB_APPROVED],
  CURRENT: [
    JobStatusDetail.PENDING_CANCELLATION,
    JobStatusDetail.IN_PROGRESS,
    JobStatusDetail.PENDING_DELIVERY_VERIFICATION,
    JobStatusDetail.PENDING_PAYMENT_VERIFICATION,
    JobStatusDetail.PAYMENT_REQUIRED,
    JobStatusDetail.DELIVERY_VERIFICATION_REJECTED,
    JobStatusDetail.REQUESTED_CANCELLATION,
    JobStatusDetail.NEEDS_RECOVERY,
    JobStatusDetail.RECOVERED,
  ],
  PAST_VC: [JobStatusDetail.PAYMENT_ISSUED, JobStatusDetail.CANCELED, JobStatusDetail.ABORTED],
  PAST: [JobStatusDetail.PAYMENT_ISSUED, JobStatusDetail.CANCELED, JobStatusDetail.ABORTED],
}

export type JOB_STATUS_DICT_KEYS = keyof typeof JOB_STATUS_DICT

export function getDefaultJobStatusDetailValue(): JobStatusDetail[] {
  return [...JOB_STATUS_DICT.CURRENT, ...JOB_STATUS_DICT.FUTURE, ...JOB_STATUS_DICT.PENDING]
}

const DEFAULT_EQUIPMENT = [
  EquipmentType.POWER_ONLY,
  EquipmentType.DRY_VAN,
  EquipmentType.FLATBED,
  EquipmentType.REEFER,
]

const today = new Date()

const ALL_DISTANCES = [DistanceTypes.SHORT, DistanceTypes.LOCAL, DistanceTypes.LONG]

export const initialQueryFilter = {
  origin_location: '',
  dest_location: ANYWHERE,
  dest_range_mi__max: DEFAULT_RADIUS,
  origin_range_mi__max: DEFAULT_RADIUS,
  equipment: DEFAULT_EQUIPMENT,
  origin_pickup_date__min: today.toString(),
  trip_distances: ALL_DISTANCES,
  booking_type: LoadBookingTypes.ALL,
}

// API

export const JOBS_SPLIT_LIST_PAGE_SIZE = 10

export interface GetJobsOptions {
  assignedTo?: string
  statusDetail?: JobStatusDetail[]
  orderBy?: string
  pageSize?: number
}

export function getJobs(page: string, options: GetJobsOptions = {}): Promise<PaginatedResults<Job>> {
  const { statusDetail, ...restOpions } = options
  const params = queryString.stringify(
    toSnakeCase({
      page,
      updated_status_details: true,
      ...(statusDetail ? { statusDetail: statusDetail.join(',') } : undefined),
      ...restOpions,
    })
  )

  return axios.get(`/api/v3/jobs/?${params}`).then((response) => {
    return {
      ...response.data,
      results: response.data.results.map((r: any) => {
        const job = toCamelCase(r)
        return {
          ...job,
          load: parseLoadDetails(job.load),
        }
      }),
    }
  })
}

export function getJobByHumanId(humanId: string) {
  return axios.get(`/api/v3/jobs/retrieve-by-human-id/?human_id=${humanId}`).then((response) => {
    return response.data
  })
}

export function getJobTrackingLocationVectors(jobId: string): Promise<PastLocationVectors> {
  let url = `/api/v3/jobs/${jobId}/past-location-vectors/`
  return axios.get(url).then((response) => {
    return toCamelCase(response.data) as PastLocationVectors
  })
}
